import { Children, cloneElement, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useRefArray } from '../../../hooks/useRefArray';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { postfixTestId } from '../../../util/testId';
import { BusyStateIndicator } from '../../BusyStateIndicator';
import { Label } from '../../Label';
import { Toolbar } from '../../Toolbar';
import { Flow } from '../Flow';
import './Form.css';
import cn from './Form.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useFormTitleId = createUseHtmlId('form-title');
export const Form = props => {
  // deconstruct props not passed through to FormFields
  const {
    className,
    style,
    title,
    toolbarItems,
    dependency,
    testId,
    role,
    ariaLabel,
    ariaLabelledBy,
    ...formFieldProps
  } = props;
  const dt = useDesignTokens();
  const formStyle = renderInlineCssVars({
    'toolbar-bd': dt.tokens['color-container-border-4'],
    'toolbar-pd': dt.tokens['space-base-s'],
    'font-weight-thick': dt.tokens['font-style-weight-700'],
    'text-transform-uppercase': dt.tokens['font-style-text-transform-uppercase']
  }, 'form');
  const items = [];
  const titleId = useFormTitleId();
  if (title) {
    items.push({
      type: 'Heading',
      id: titleId,
      level: 6,
      children: title,
      className: cn.title
    });
    items.push({
      type: 'ToolbarSpacer'
    });
  }
  if (toolbarItems) {
    items.push(...toolbarItems);
  }
  return /*#__PURE__*/_jsxs(Flow, {
    orientation: "vertical",
    className: cx(className, props.dependency && cn.dependency),
    style: {
      ...formStyle,
      ...style
    },
    "data-testid": testId,
    role: role ?? items.length > 0 ? 'group' : undefined,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy !== undefined ? ariaLabelledBy : title ? titleId : undefined,
    children: [items.length > 0 ? /*#__PURE__*/_jsx(Toolbar, {
      items: items,
      className: cn.toolbar,
      ariaLabelledby: title ? '' : undefined
    }) : null, /*#__PURE__*/_jsx(FormFields, {
      testId: postfixTestId(testId, 'form-fields'),
      ...formFieldProps,
      children: props.children
    })]
  });
};
const FormFields = ({
  labelPosition = 'auto',
  breakpoint = 600,
  fieldSpacing = 'm',
  labelSpacing,
  children,
  fieldLabelWidth,
  busyStateIndicatorProps,
  disabled,
  testId
}) => {
  const dt = useDesignTokens();
  const [formLessThanBreakpoint, setFormLessThanBreakpoint] = useState(false);
  const topAligned = labelPosition === 'top' || (labelPosition === 'auto' ? formLessThanBreakpoint : false);
  const [containerIsReady, setContainerIsReady] = useState(false);
  const [longestLabelWidth, setLongestLabelWidth] = useState(undefined);
  const {
    refs: formLabelsRefs,
    createSetRef: createFormLabelRef
  } = useRefArray();
  const labelRef = useRef(null);
  function updateFormAlignment() {
    const formContainer = getFormContainer();
    if (!formContainer) return;
    setFormLessThanBreakpoint(formContainer.offsetWidth < breakpoint);
  }
  function updateContainerIsReady() {
    setContainerIsReady((getFormContainer()?.offsetWidth ?? 0) > 0);
  }
  const [setFormContainer, getFormContainer] = useResizeObserverRef(() => {
    updateFormAlignment();
    updateContainerIsReady();
  });
  const calculateLongestLabel = labelPosition !== 'top' && !fieldLabelWidth && containerIsReady;
  const [showBusyIndicator, setShowBusyIndicator] = useState(true);
  useEffect(() => {
    if (longestLabelWidth || containerIsReady) {
      setShowBusyIndicator(false);
    }
  }, [longestLabelWidth, containerIsReady]);
  useEffect(() => {
    setContainerIsReady((getFormContainer()?.offsetWidth ?? 0) > 0);
  }, [getFormContainer]);
  const computedLabelSpacing = labelSpacing?.leftAligned ? dt.spacing.core[labelSpacing?.leftAligned] : dt.tokens['space-base-m'];
  // FIXME: formLabelRefs doesn't change if the label is dynamically updated after first render
  useEffect(() => {
    // If the Form is top aligned or if a fieldLabelWidth is provided we don't need to measure the longest label.
    if (formLabelsRefs.length < 1 || !calculateLongestLabel) {
      setLongestLabelWidth(undefined);
      return;
    }
    let longestLabelWidth = 0;
    if (labelRef.current !== null) {
      labelRef.current.style.whiteSpace = 'nowrap';
    }
    for (const formLabelRef of formLabelsRefs) {
      if (!formLabelRef) continue;
      labelRef.current?.replaceChildren(...formLabelRef.cloneNode(true).childNodes);
      const width = Math.ceil((labelRef.current?.getBoundingClientRect().width ?? 0) + 2);
      longestLabelWidth = Math.max(width, longestLabelWidth);
    }
    labelRef.current?.replaceChildren('');
    if (longestLabelWidth > 0) {
      longestLabelWidth += parseFloat(computedLabelSpacing) * 16; // Add 16px to account for container padding
    }

    setLongestLabelWidth(longestLabelWidth);
  }, [calculateLongestLabel, formLabelsRefs, labelSpacing, computedLabelSpacing]);
  const newChildren = useMemo(() => Children.toArray(children).map((child, index) => {
    let newFieldLabelWidth;
    if (!topAligned && fieldLabelWidth) {
      newFieldLabelWidth = fieldLabelWidth;
    } else if (longestLabelWidth) {
      newFieldLabelWidth = `${longestLabelWidth}px`;
    }

    // withLabels explicitly defines the 'displayName' and includes 'WithLabels' in that name.
    // @ts-ignore
    if (child.type.displayName?.includes('WithLabels')) {
      return /*#__PURE__*/cloneElement(child, {
        key: index,
        labelPosition: topAligned ? 'top' : 'left',
        fieldLabelWidth: newFieldLabelWidth,
        labelContainerRef: createFormLabelRef(index),
        labelSpacing,
        // Clone children will override the child's disabled prop if it's passed as undefined here
        ...(disabled ? {
          disabled
        } : {})
      });
    } else {
      return /*#__PURE__*/_jsx("div", {
        className: cn.item,
        style: !topAligned ? {
          paddingInlineStart: fieldLabelWidth ? fieldLabelWidth : longestLabelWidth
        } : undefined,
        children: child
      }, index);
    }
  }), [children, fieldLabelWidth, topAligned, createFormLabelRef, labelSpacing, longestLabelWidth, disabled]);
  return /*#__PURE__*/_jsx(BusyStateIndicator, {
    disableAutoFocus: true,
    active: showBusyIndicator,
    fillParentContainer: true,
    testId: postfixTestId(testId, 'busy-state-indicator'),
    ...busyStateIndicatorProps,
    children: /*#__PURE__*/_jsxs(Flow, {
      orientation: "vertical",
      ref: setFormContainer,
      className: cn.formFields,
      spacing: fieldSpacing,
      style: {
        opacity: showBusyIndicator ? 0 : 1
      },
      "data-testid": postfixTestId(testId, 'flow'),
      children: [/*#__PURE__*/_jsx(Label, {
        style: {
          visibility: 'hidden',
          position: 'absolute'
        },
        ref: labelRef
      }), newChildren]
    })
  });
};
//# sourceMappingURL=Form.js.map